import type { App, Directive, DirectiveBinding } from 'vue';
import { hasAnyPerm, hasPerm } from './permission';

/**
 * v-perm="'order:assign'"        单个权限码
 * v-perm="['order:assign','x']"  任一命中即可（or 语义）
 * v-perm:disable="'order:cancel'" 无权限时禁用而非隐藏（保留可发现性 + tooltip）
 *
 * 归属口径：本指令只管它自己写下的禁用/隐藏状态。
 * disabled 与 title 常常是调用方用 :disabled / :title 绑上来的，而 Vue 在指令的 updated
 * 之前就把 props patch 完了 —— 于是「有权限」分支里无条件清 disabled / title，会把调用方
 * 的绑定一并抹掉（按钮看着能点、提示也没了；5173 角色权限屏的保存按钮实机就是这样）。
 * 因此：接管前先把调用方的原值记下来（WeakMap 快照 + data-perm-owned 归属标记），回收时再
 * 比对「当前值是否还是本指令写的那份」，只清自己的；Vue 在接管之后写过的值原样保留。
 */

type PermValue = string | string[] | null | undefined;

/** 归属标记：本指令此刻在这枚元素上写的状态（disable＝禁用 / hide＝隐藏） */
const OWNED_KEY = 'permOwned';
/** v-perm:disable 无权限时写进去的 title 文案 */
const DISABLED_TOOLTIP = '无操作权限，请联系管理员分配';
/** 本指令写进去的 disabled 字面值（Vue 的布尔属性写的是空串，据此区分谁写的） */
const DISABLED_ATTR_VALUE = 'disabled';

type OwnedKind = 'disable' | 'hide';

interface OwnedState {
  kind: OwnedKind;
  /** 以下几项是接管前的值，属于调用方（Vue 绑定或模板静态属性），回收时原样还回 */
  hadDisabled: boolean;
  disabled: string | null;
  hadTitle: boolean;
  title: string | null;
  isDisabled: boolean;
  pointerEvents: string;
  display: string;
}

const ownedState = new WeakMap<HTMLElement, OwnedState>();

/** 接管前记快照；已经在接管中就不再记第二遍（否则会把本指令写的值当成调用方的原值） */
function snapshot(el: HTMLElement, kind: OwnedKind): void {
  if (ownedState.has(el)) return;
  ownedState.set(el, {
    kind,
    hadDisabled: el.hasAttribute('disabled'),
    disabled: el.getAttribute('disabled'),
    hadTitle: el.hasAttribute('title'),
    title: el.getAttribute('title'),
    isDisabled: el.classList.contains('is-disabled'),
    pointerEvents: el.style.pointerEvents,
    display: el.style.display,
  });
  el.dataset[OWNED_KEY] = kind;
}

/** 按快照把属性还给调用方（原先没有就删掉） */
function giveBackAttr(el: HTMLElement, key: 'disabled' | 'title', had: boolean, value: string | null): void {
  if (had && value !== null) el.setAttribute(key, value);
  else el.removeAttribute(key);
}

/** 按快照把行内样式还给调用方（原先没写就删掉该属性） */
function giveBackStyle(el: HTMLElement, prop: 'pointerEvents' | 'display', cssProp: string, value: string): void {
  if (value === '') el.style.removeProperty(cssProp);
  else el.style[prop] = value;
}

/** 回收本指令写下的状态；调用方自己的 disabled / title / 行内样式原样不动 */
function reclaim(el: HTMLElement): void {
  const owned = ownedState.get(el);
  if (!owned) {
    delete el.dataset[OWNED_KEY];
    return;
  }
  if (owned.kind === 'disable') {
    // 当前值仍是本指令写的那份才回收；Vue（含 Element Plus）在接管之后重新写过的值原样保留
    if (el.getAttribute('disabled') === DISABLED_ATTR_VALUE) {
      giveBackAttr(el, 'disabled', owned.hadDisabled, owned.disabled);
      // is-disabled 与 disabled 同源：调用方自己那枚 is-disabled 不动
      if (!owned.isDisabled) el.classList.remove('is-disabled');
    }
    if (el.getAttribute('title') === DISABLED_TOOLTIP) giveBackAttr(el, 'title', owned.hadTitle, owned.title);
    if (el.style.pointerEvents === 'none') giveBackStyle(el, 'pointerEvents', 'pointer-events', owned.pointerEvents);
  } else if (el.style.display === 'none') {
    giveBackStyle(el, 'display', 'display', owned.display);
  }
  ownedState.delete(el);
  delete el.dataset[OWNED_KEY];
}

function evaluate(value: PermValue): boolean {
  if (Array.isArray(value)) return hasAnyPerm(value);
  return hasPerm(value);
}

function apply(el: HTMLElement, binding: DirectiveBinding<PermValue>): void {
  const allowed = evaluate(binding.value);
  if (allowed) {
    reclaim(el);
    return;
  }

  if (binding.arg === 'disable') {
    reclaim(el); // 先还掉上一次接管的痕迹，再按当前语义接管
    snapshot(el, 'disable');
    el.setAttribute('disabled', DISABLED_ATTR_VALUE);
    el.classList.add('is-disabled');
    el.setAttribute('title', DISABLED_TOOLTIP);
    el.style.pointerEvents = 'none';
    return;
  }

  reclaim(el);
  snapshot(el, 'hide');
  el.style.display = 'none';
}

export const permDirective: Directive<HTMLElement, PermValue> = {
  mounted: apply,
  updated: apply,
};

export function installPermissionDirectives(app: App): void {
  app.directive('perm', permDirective);
}
